import React from "react";

/**
 * WECOCKPIT — Logo officiel.
 *
 * Concept : un carré arrondi navy (couleur Hub) contient "we" en blanc bold
 * lowercase + un point d'accent sky en haut à droite (= le spot "cockpit").
 * À côté, le wordmark "WECOCKPIT" — "we" en regular pour effet de continuité,
 * "COCKPIT" en black/extra-bold.
 *
 * Variantes :
 *   - mark      : juste le carré (favicon, badges, mobile compact)
 *   - lockup    : mark + wordmark horizontal (headers, login)
 *   - stacked   : mark au-dessus + wordmark dessous (splash, hero)
 *   - wordmark  : juste le texte (footer, attribution)
 *
 * Le composant est full SVG inline, accessible (role/aria), responsive (size en px).
 */
export function WeCockpitLogo({
  variant = "lockup",
  size = 32,                  // hauteur en px
  tone = "dark",              // dark | light
  className = "",
  withTagline = false,
  tagline = "Pilotons ensemble votre présence digitale",
  ...rest
}) {
  // Palette — cercle BLANC + "WE" NAVY (identité de marque définitive)
  const circleFill = "#FFFFFF";
  const weFill     = "#0F2744";
  const cursorStroke = "#0F2744";
  const ink    = tone === "light" ? "#0F2744" : "#FFFFFF";
  const accent = "#0EA5E9";   // sky-500

  // Mark — cercle blanc parfait + "WE" navy + curseur SKY qui dépasse en bas-droite
  const Mark = ({ s = size }) => (
    <svg
      width={s} height={s} viewBox="0 0 120 120"
      role="img" aria-label="WECOCKPIT"
      className="flex-shrink-0"
      data-testid="wecockpit-mark"
    >
      {/* Cercle parfait blanc + bord navy fin pour visibilité sur fond clair */}
      <circle cx="58" cy="58" r="50" fill={circleFill} stroke="#0F2744" strokeOpacity="0.18" strokeWidth="1.5"/>

      {/* "WE" parfaitement centré, navy, weight 800 Poppins */}
      <text
        x="58" y="74"
        textAnchor="middle"
        fontFamily='"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        fontWeight="800"
        fontSize="48"
        letterSpacing="-2"
        fill={weFill}
      >WE</text>

      {/* Curseur de souris classique (tip top-left), sky blue, dépasse du cercle en bas-droite */}
      <g transform="translate(72 64)">
        <path
          d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z"
          transform="scale(2.2)"
          fill={accent}
          stroke={cursorStroke}
          strokeWidth="0.6"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </svg>
  );

  // Wordmark — "COCKPIT" en sky avec le O remplacé par un cercle parfait CSS
  // (taille en em → matche exactement la cap-height des autres lettres ; bordure plus fine pour matcher le weight 500)
  const RoundO = ({ color }) => (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: "0.72em",
        height: "0.72em",
        border: "0.085em solid " + color,
        borderRadius: "50%",
        verticalAlign: "-3%",
        boxSizing: "border-box",
        margin: "0 -0.02em",
      }}
    />
  );

  // Wordmark — "COCKPIT" en sky avec font Poppins (cohérent avec font-display de la marque)
  const Wordmark = ({ scale = 1, standalone = false }) => {
    const fontSize = Math.round(size * 0.78 * scale);
    const cockpitStyle = {
      fontFamily: '"Poppins", "Outfit", ui-sans-serif, system-ui, sans-serif',
      color: accent,
      fontWeight: 800,
      letterSpacing: "-0.04em",
    };
    if (standalone) {
      return (
        <span
          className="inline-flex items-baseline leading-none select-none"
          style={{ fontSize }}
          data-testid="wecockpit-wordmark"
        >
          <span style={{
            fontFamily: '"Poppins", "Outfit", ui-sans-serif, system-ui, sans-serif',
            color: ink, fontWeight: 800, letterSpacing: "-0.04em",
          }}>WE</span>
          <span style={{ ...cockpitStyle, marginLeft: 4 }}>COCKPIT</span>
        </span>
      );
    }
    // Variante lockup : "COCKPIT" en Poppins (toutes lettres même typo, le O est naturellement géométrique)
    return (
      <span
        className="inline-flex items-baseline leading-none select-none"
        style={{ fontSize, ...cockpitStyle }}
        data-testid="wecockpit-wordmark"
      >
        COCKPIT
      </span>
    );
  };

  if (variant === "mark") return <Mark {...rest} />;

  if (variant === "wordmark") {
    return (
      <span className={`inline-flex flex-col gap-0.5 ${className}`} {...rest}>
        <Wordmark standalone/>
        {withTagline && (
          <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: tone === "light" ? "#CBD5E1" : "#64748B" }}>
            {tagline}
          </span>
        )}
      </span>
    );
  }

  if (variant === "stacked") {
    return (
      <div className={`inline-flex flex-col items-center gap-2 ${className}`} {...rest}>
        <Mark s={Math.round(size * 1.6)}/>
        <Wordmark scale={1.1}/>
        {withTagline && (
          <span className="text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: tone === "light" ? "#CBD5E1" : "#64748B" }}>
            {tagline}
          </span>
        )}
      </div>
    );
  }

  // default lockup horizontal — gap réduit entre le mark "WE" et le wordmark "COCKPIT"
  return (
    <div className={`inline-flex items-center gap-1 ${className}`} {...rest}>
      <Mark/>
      <span className="inline-flex flex-col gap-0.5 -ml-0.5">
        <Wordmark/>
        {withTagline && (
          <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: tone === "light" ? "#CBD5E1" : "#64748B" }}>
            {tagline}
          </span>
        )}
      </span>
    </div>
  );
}

export default WeCockpitLogo;
